/**
 * Workflow Progress Component
 *
 * Shows the progress bar for the setup wizard workflow:
 * Upload -> Decompose -> Research -> Plan -> Configure -> Verify -> Report
 */

import React from 'react';

export type WorkflowStep =
  | 'upload'
  | 'decompose'
  | 'research'
  | 'plan'
  | 'configure'
  | 'verify'
  | 'report';

interface WorkflowProgressProps {
  currentStep: WorkflowStep;
  completedSteps: WorkflowStep[];
}

const STEPS: { id: WorkflowStep; label: string }[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'decompose', label: 'Decompose' },
  { id: 'research', label: 'Research' },
  { id: 'plan', label: 'Plan' },
  { id: 'configure', label: 'Configure' },
  { id: 'verify', label: 'Verify' },
  { id: 'report', label: 'Report' },
];

export function WorkflowProgress({ currentStep, completedSteps }: WorkflowProgressProps) {
  const getStepStatus = (step: WorkflowStep): 'completed' | 'current' | 'pending' => {
    if (completedSteps.includes(step)) return 'completed';
    if (step === currentStep) return 'current';
    return 'pending';
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const status = getStepStatus(step.id);
          const isLast = index === STEPS.length - 1;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                {/* Step indicator */}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    status === 'completed'
                      ? 'bg-green-600 text-white'
                      : status === 'current'
                      ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {status === 'completed' ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : status === 'current' ? (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  ) : (
                    <span className="text-xs">{index + 1}</span>
                  )}
                </div>

                {/* Step label */}
                <span
                  className={`mt-2 text-xs font-medium ${
                    status === 'completed'
                      ? 'text-green-400'
                      : status === 'current'
                      ? 'text-blue-400'
                      : 'text-gray-500'
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${
                    completedSteps.includes(step.id) ? 'bg-green-600' : 'bg-gray-700'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
